/**
 * Font Awesome Pro icon wrapper.
 *
 * Usage:
 *   <Icon name="inbox" />                    // default: duotone
 *   <Icon name="magnifying-glass" variant="solid" />
 *   <Icon name="github" variant="brands" />
 *   <Icon name="spinner-third" variant="solid" spin />
 *
 * The underlying FA classes (`fa-duotone fa-inbox`) are still usable
 * directly on any <i>; this component just gives us a typed seam so
 * we can globally swap icon library later (e.g., back to lucide) by
 * editing this one file.
 *
 * Style families loaded in index.html: solid, regular, duotone, brands.
 * To use sharp/jelly/etch/notdog/chisel/utility/etc., add the matching
 * <link> in index.html first; each adds 5–20 KB.
 */

import { forwardRef, type HTMLAttributes } from 'react'
import { cn } from '@/lib/utils'

export type IconVariant =
  | 'solid'
  | 'regular'
  | 'light'
  | 'thin'
  | 'duotone'
  | 'brands'
  | 'sharp'
  | 'sharp-duotone'

export interface IconProps extends HTMLAttributes<HTMLElement> {
  name: string
  variant?: IconVariant
  /** `fa-spin` — continuous rotation. Use for loaders. */
  spin?: boolean
  /** `fa-pulse` — discrete 8-step rotation. Subtler than spin. */
  pulse?: boolean
  /** `fa-fw` — fixed-width slot. Useful when icons sit in a column. */
  fixedWidth?: boolean
  /** Inherit size from parent. By default we use `1em`. */
  inheritSize?: boolean
}

const variantClass: Record<IconVariant, string> = {
  solid: 'fa-solid',
  regular: 'fa-regular',
  light: 'fa-light',
  thin: 'fa-thin',
  duotone: 'fa-duotone',
  brands: 'fa-brands',
  sharp: 'fa-sharp fa-solid',
  'sharp-duotone': 'fa-sharp fa-duotone',
}

export const Icon = forwardRef<HTMLElement, IconProps>(
  (
    {
      name,
      variant = 'duotone',
      spin,
      pulse,
      fixedWidth,
      inheritSize,
      className,
      ...rest
    },
    ref,
  ) => (
    <i
      ref={ref}
      aria-hidden="true"
      className={cn(
        variantClass[variant],
        `fa-${name}`,
        spin && 'fa-spin',
        pulse && 'fa-pulse',
        fixedWidth && 'fa-fw',
        !inheritSize && 'leading-none',
        className,
      )}
      {...rest}
    />
  ),
)
Icon.displayName = 'Icon'
